/* eslint-disable react/prop-types */
import React, { useReducer, useEffect } from 'react';
import {
    useQuery,
    useLazyQuery,
    useApolloClient,
    useSubscription,
} from '@apollo/client';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles, Checkbox,
    TextField, Button,
    useMediaQuery,
} from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash';
import useImagesActions from 'components/modules/inventory/hooks/useImagesActions';
import useReconActions from 'components/modules/inventory/hooks/useReconActions';
import { ReconStatus } from 'utils/enum/InventoryEnum';
import { FetchPolicy } from 'utils/enum/Core';
import Switch from '@material-ui/core/Switch';
import { useTheme } from '@material-ui/core/styles';
import ModalUtils from 'utils/ModalUtils';
import ArrayUtils from 'lib/ArrayUtils';
import ButtonStyles from 'styles/theme/Button';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import InventoryModuleSubscription from 'services/graphQL/subscription/inventory/InventoryModuleSubscription';
import VirtualTable from 'components/widgets/VirtualTable';
import InputNumber from 'components/widgets/InputNumber';
import If from 'components/widgets/conditional/If';
import InspectionImagesDialog from 'components/modules/inventory/read/InspectionImagesDialog';
import InspectionCategoryPanel from 'components/modules/inventory/read/panels/InspectionCategoryPanel';
import ReconActionsBar from 'components/modules/inventory/read/ReconActionsBar';

const INIT_STATE = {
    categories: [],
    status: null,
    note: null,
    inspector: null,
    inspectors: [],
    repairsSummary: null,
    isPicturesFormOpen: false,
    isUploadingImages: false,
    isSavingInspectionItems: false,
    selectedItem: null,
    selectedItemImages: null,
    openImagePreview: false,
    photoIndex: -1,
    itemsForRepair: null,
};

const ACTION_TYPES = {
    SET_CATEGORIES: 'setCategories',
    INIT_RECON_STATUS: 'initReconStatus',
    TOGGLE_IS_PICTURES_FORM_OPEN: 'toggleIsPicturesFormOPen',
    TOGGLE_UPLOADING_IMAGES: 'toggleUploadingImages',
    SET_SELECTED_ITEM_IMAGES: 'setSelectedItemImages',
    TOGGLE_IMAGE_PREVIEW: 'toggleImagePreview',
    TOGGLE_IS_SAVING_INSPECTION_ITEMS: 'toggleIsSavingInspectionItems',
    SET_PHOTO_INDEX: 'setPhotoIndex',
    SET_RECON_STATUS: 'setReconStatus',
    SET_CATEGORIES_AND_ITEMS_FOR_REPAIR: 'setCategoriesAndItemsForRepair',
};

const reducer = (state, action = {}) => {
    switch (action.type) {
    case ACTION_TYPES.SET_CATEGORIES:
        return {
            ...state,
            categories: action.value,
        };
    case ACTION_TYPES.INIT_RECON_STATUS:
        const {
            status,
            note,
            currentInspector,
            inspectors,
            repairsSummary,
            roCreated,
            roNumber,
        } = action.value;

        let allInspectors = inspectors;
        if (currentInspector && !allInspectors.find((i) => i.userID === currentInspector.userID)) {
            allInspectors = [{ ...currentInspector, default: false }, ...allInspectors];
        }

        return {
            ...state,
            status,
            note,
            inspector: currentInspector?.userID,
            inspectors: allInspectors,
            repairsSummary,
            roCreated,
            roNumber,
        };
    case ACTION_TYPES.SET_RECON_STATUS:
        return {
            ...state,
            status: action.value.status,
            inspector: action.value.inspector,
        };
    case ACTION_TYPES.TOGGLE_IS_PICTURES_FORM_OPEN:
        return {
            ...state,
            isPicturesFormOpen: !state.isPicturesFormOpen,
            selectedItem: action.value,
            selectedItemImages: (!action.value ? null : state.selectedItemImages),
        };
    case ACTION_TYPES.TOGGLE_UPLOADING_IMAGES:
        return {
            ...state,
            isUploadingImages: !state.isUploadingImages,
        };
    case ACTION_TYPES.SET_SELECTED_ITEM_IMAGES:
        return {
            ...state,
            selectedItemImages: action.value,
        };
    case ACTION_TYPES.TOGGLE_IMAGE_PREVIEW:
        return {
            ...state,
            openImagePreview: !state.openImagePreview,
            photoIndex: action.value,
        };
    case ACTION_TYPES.TOGGLE_IS_SAVING_INSPECTION_ITEMS:
        return {
            ...state,
            isSavingInspectionItems: !state.isSavingInspectionItems,
        };
    case ACTION_TYPES.SET_PHOTO_INDEX:
        return {
            ...state,
            photoIndex: action.value,
        };
    case ACTION_TYPES.SET_CATEGORIES_AND_ITEMS_FOR_REPAIR:
        const { categories, itemsForRepair } = action.value;

        return {
            ...state,
            categories,
            itemsForRepair,
        };
    default:
        return state;
    }
};

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    main: {
        display: 'flex',
        position: 'relative',
        minWidth: '1020px',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            marginTop: '20px',
            minWidth: 'initial',
        },
    },
    tableContainer: {
        marginTop: '15px',
        paddingLeft: '15px',
        paddingRight: '15px',
        paddingBottom: '20px',
        height: '81vh',
        width: '100%',
        minWidth: '900px',
        overflow: 'hidden',
        '& .ReactVirtualized__Table__headerRow': {
            display: 'none',
        },
        '& .ReactVirtualized__Table__Grid': {
            paddingBottom: '100px',
        },
    },
    categoryText: {
        color: theme.palette.text.white,
        fontWeight: 'bold',
    },
    textFieldSmall: {
        '& input': {
            padding: 7,
        },
        width: '100%',
    },
    uploadButton: {
        fontSize: '12px',
        borderRadius: '0px',
    },
    greenUploadButton: {
        backgroundColor: `${theme.palette.background.green} !important`,
    },
    passCheck: {
        color: theme.palette.text.green,
    },
    failCheck: {
        color: theme.palette.text.red,
    },
    requiredEstimate: {
        '& > input': {
            borderColor: theme.palette.background.red,
        },
    },
}));

const InspectionListTab = ({
    stockNumber,
    canWrite,
    canApprove,
    saveRepairItem,
    maximumReconCost,
    showROAction,
}) => {
    const client = useApolloClient();
    const classes = { ...useStyles(), ...buttonStyles() };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {
        moveType, addImages, deleteImages, setPhotoIndex,
    } = useImagesActions();
    const { changeReconStatus, savingReconStatus } = useReconActions();
    const [state, dispatch] = useReducer(reducer, INIT_STATE);
    const {
        categories,
        status,
        note,
        isPicturesFormOpen,
        isUploadingImages,
        isSavingInspectionItems,
        selectedItem,
        selectedItemImages,
        openImagePreview,
        photoIndex,
        inspector,
        inspectors,
        itemsForRepair,
        repairsSummary,
        roCreated,
        roNumber,
    } = state;

    const {
        data: statusData,
        loading: loadingStatus,
        error: errorStatus,
        refetch: refetchReconStatus,
    } = useQuery(InventoryQuery.GET_RECON_STATUS, {
        variables: {
            stockNumber,
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        notifyOnNetworkStatusChange: true,
    });

    const {
        data: inspectionListItemsData,
        loading: loadingInspectionListItems,
        error: errorInspectionListItems,
    } = useQuery(InventoryQuery.GET_INSPECTION_LIST_ITEMS, {
        variables: {
            stockNumber,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const { data: subscriptionData } = useSubscription(
        InventoryModuleSubscription.INSPECTION_ITEM_IMAGES_UPDATED,
        { variables: { stockNumber }, skip: !stockNumber },
    );

    const [getImages] = useLazyQuery(InventoryQuery.GET_VEHICLE_RECON_ITEM_IMAGES, {
        onCompleted: (response) => {
            if (response) {
                const images = response.getVehicleReconItemImages;
                dispatch({
                    type: ACTION_TYPES.SET_SELECTED_ITEM_IMAGES,
                    value: images,
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const setRepairItemIds = (repairableItems, cats) => {
        const clone = cloneDeep(cats || categories);

        clone.forEach((cat) => {
            const { items } = cat;

            items.forEach((item) => {
                const { reconInspectionItemId } = item;
                const element = repairableItems.find((ri) => ri.reconInspectionItemId === reconInspectionItemId);

                if (element) {
                    // eslint-disable-next-line no-param-reassign
                    item.vehicleRepairItemId = element.vehicleRepairItemId;
                }
            });
        });

        dispatch({
            type: ACTION_TYPES.SET_CATEGORIES,
            value: clone,
        });
    };

    const getItemsToRepair = (cats = []) => {
        const items = [];
        cats.forEach((cat) => {
            if (ArrayUtils.isNotEmpty(cat.items)) {
                cat.items.forEach((item) => {
                    if (item.current && item.current.repair) items.push(item);
                });
            }
        });

        return items;
    };

    const formatForInspectionSheet = (cats = []) => {
        let output = [];
        cats.forEach((cat) => {
            if (ArrayUtils.isNotEmpty(cat.items)) {
                output.push({
                    reconInspectionCategoryId: cat.reconInspectionCategoryId,
                    name: cat.name,
                    order: cat.order,
                    active: cat.active,
                });
                output = [...output, ...cat.items];
            }
        });

        return output;
    };

    const formattedData = formatForInspectionSheet(categories);
    const rowStyleFormat = (row) => {
        const { index } = row;
        if (index < 0) return;

        const record = formattedData[index];
        const isCategory = 'reconInspectionCategoryId' in record;
        if (!isCategory) return;

        // eslint-disable-next-line consistent-return
        return {
            pointerEvents: 'none',
            backgroundColor: theme.palette.background.darkPurple,
        };
    };

    useEffect(() => {
        if (errorInspectionListItems) {
            ModalUtils.errorMessage(errorInspectionListItems?.graphQLErrors);
        }

        if (!loadingInspectionListItems) {
            const data = (inspectionListItemsData?.getInspectionListItems ?? [])
                .filter((item) => !item.forTechnicians);

            dispatch({
                type: ACTION_TYPES.SET_CATEGORIES_AND_ITEMS_FOR_REPAIR,
                value: {
                    categories: data,
                    itemsForRepair: getItemsToRepair(data),
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingInspectionListItems, errorInspectionListItems]);

    useEffect(() => {
        const result = subscriptionData?.inspectionItemImagesUpdated;
        if (isEmpty(result)) return;

        let catIdx = -1;
        let itemIdx = -1;
        for (let c = 0; c < categories.length; c += 1) {
            const catItems = categories[c].items;
            if (catItems?.length) {
                const idx = catItems.findIndex(
                    (i) => i.reconInspectionItemId === result.reconInspectionItemId
                        || (result.vehicleInspectionItemId
                            && i.current?.vehicleInspectionItemId === result.vehicleInspectionItemId),
                );
                if (idx >= 0) {
                    catIdx = c;
                    itemIdx = idx;
                    break;
                }
            }
        }

        if (catIdx >= 0) {
            const cat = categories[catIdx];
            const newItems = cat.items.map((item, i) => (
                i === itemIdx ? { ...item, images: result.imageCount } : item
            ));
            const nextCategories = [
                ...categories.slice(0, catIdx),
                { ...cat, items: newItems },
                ...categories.slice(catIdx + 1),
            ];
            dispatch({
                type: ACTION_TYPES.SET_CATEGORIES,
                value: nextCategories,
            });
        }

        const openVehicleId = selectedItem?.current?.vehicleInspectionItemId;
        const dialogMatchesItem = isPicturesFormOpen && selectedItem && (
            (result.vehicleInspectionItemId && openVehicleId === result.vehicleInspectionItemId)
            || selectedItem.reconInspectionItemId === result.reconInspectionItemId
        );
        if (dialogMatchesItem && openVehicleId) {
            getImages({
                variables: {
                    vehicleInspectionItemId: openVehicleId,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptionData]);

    useEffect(() => {
        if (itemsForRepair && itemsForRepair.length > 0) {
            try {
                client.query({
                    query: InventoryQuery.GET_REPAIR_ITEMS,
                    variables: { stockNumber },
                    fetchPolicy: FetchPolicy.NETWORK_ONLY,
                }).then(async (response) => {
                    const { data } = response;

                    const result = data?.getRepairItems;
                    if (result && result.length > 0) {
                        const repairableItems = [];
                        result.forEach((e) => {
                            const { vehicleRepairItemId, inspectionItem: { reconItem: { reconInspectionItemId } } } = e;

                            repairableItems.push({
                                vehicleRepairItemId,
                                reconInspectionItemId,
                            });
                        });

                        setRepairItemIds(repairableItems);
                    }
                });
            } catch (error) {
                ModalUtils.errorMessage(null, error.message);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsForRepair]);

    useEffect(() => {
        if (errorStatus) {
            ModalUtils.errorMessage(errorStatus?.graphQLErrors);
        }

        if (!loadingStatus) {
            const data = statusData?.getReconStatus;
            if (data) {
                dispatch({
                    type: ACTION_TYPES.INIT_RECON_STATUS,
                    value: data,
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingStatus, errorStatus]);

    const saveReconItems = async (showSucessMessage = true) => {
        if (status === ReconStatus.INSPECTION_APPROVED || status === ReconStatus.REPAIRS_COMPLETE || !canWrite || isSavingInspectionItems) return;

        try {
            dispatch({
                type: ACTION_TYPES.TOGGLE_IS_SAVING_INSPECTION_ITEMS,
            });

            const references = [];
            const itemsToUpdate = [];
            const clone = cloneDeep(categories);
            let anyNew = false;
            clone.forEach((cat) => {
                const { items } = cat;

                items.forEach((item) => {
                    const { reconInspectionItemId, name, current } = item;

                    if (current && current.modified) {
                        if (!current.vehicleInspectionItemId) anyNew = true;
                        references.push(item);

                        itemsToUpdate.push({
                            vehicleInspectionItemId: current.vehicleInspectionItemId || null,
                            reconInspectionItemId,
                            reconInspectionItemName: name,
                            pass: current.pass || false,
                            fail: current.fail || false,
                            part: current.part || false,
                            repair: current.repair || false,
                            estimation: current.estimation || null,
                            comment: current.comment || null,
                        });
                    }
                });
            });

            if (showSucessMessage && itemsToUpdate.length === 0) {
                dispatch({
                    type: ACTION_TYPES.TOGGLE_IS_SAVING_INSPECTION_ITEMS,
                });

                ModalUtils.errorMessage(null, 'No items have been modified');
                return;
            }

            const { data } = await client.mutate({
                mutation: InventoryMutation.SAVE_VEHICLE_RECON_ITEMS,
                variables: {
                    stockNumber,
                    items: itemsToUpdate,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            if (showSucessMessage) {
                ModalUtils.successMessage(null, 'Sheet saved successfully');

                // Reset Modified Items
                references.forEach((e) => { if (e.current.modified) { e.current.modified = false; } return e; });
                if (!anyNew) {
                    dispatch({
                        type: ACTION_TYPES.SET_CATEGORIES,
                        value: clone,
                    });
                }
            }

            if (anyNew) {
                const response = data?.saveVehicleReconItems;
                response.forEach((el) => {
                    const { vehicleInspectionItemId, reconInspectionItemId } = el;

                    const item = references.find((ref) => ref.reconInspectionItemId === reconInspectionItemId);
                    if (item) {
                        item.current.vehicleInspectionItemId = vehicleInspectionItemId;
                    }
                });

                dispatch({
                    type: ACTION_TYPES.SET_CATEGORIES,
                    value: clone,
                });
            }

            const repairItemstoUpdate = [];
            const repairableItemsIds = [];
            references.forEach((item) => {
                const { vehicleRepairItemId, reconInspectionItemId, current: { vehicleInspectionItemId, comment, repair } } = item;

                if (
                    (repair && !vehicleRepairItemId)
                    || (!repair && vehicleRepairItemId)
                ) {
                    repairableItemsIds.push({
                        vehicleRepairItemId: null,
                        reconInspectionItemId,
                    });

                    repairItemstoUpdate.push({
                        vehicleRepairItemId,
                        vehicleInspectionItemId,
                        order: repair && !vehicleRepairItemId ? 0 : -1,
                        assigneeId: null,
                        vendorId: null,
                        startOn: null,
                        completeOn: null,
                        comment,
                        active: (repair && !vehicleRepairItemId),
                    });
                }
            });

            if (repairItemstoUpdate.length > 0) {
                const ids = await ArrayUtils.processPromisesInSequence(repairItemstoUpdate, saveRepairItem);
                ids.forEach((id, index) => {
                    const element = repairItemstoUpdate[index];
                    if (element.active) repairableItemsIds[index].vehicleRepairItemId = id;
                });

                setRepairItemIds(repairableItemsIds, clone);
            }

            dispatch({
                type: ACTION_TYPES.TOGGLE_IS_SAVING_INSPECTION_ITEMS,
            });
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        }
    };

    const onChange = (name, value, itemId) => {
        if (status === ReconStatus.INSPECTION_APPROVED || status === ReconStatus.REPAIRS_COMPLETE || !canWrite) return;

        try {
            const clone = cloneDeep(categories);
            for (let x = 0; x < clone.length; x += 1) {
                const cat = clone[x];
                const { items } = cat;

                const index = items.findIndex((item) => item.reconInspectionItemId === itemId);
                if (index >= 0) {
                    let others = {};
                    switch (`${name}-${value}`) {
                    case 'pass-true':
                        others = {
                            fail: false,
                            part: false,
                            repair: false,
                            estimation: 0,
                        };
                        break;
                    case 'fail-true':
                        others = {
                            pass: false,
                        };
                        break;
                    case 'fail-false':
                        others = {
                            pass: false,
                            part: false,
                            repair: false,
                            estimation: 0,
                        };
                        break;
                    default:
                        break;
                    }

                    items[index].current = {
                        ...(items[index].current || {}),
                        [name]: value,
                        modified: true,
                        ...others,
                    };
                    break;
                }
            }

            dispatch({
                type: ACTION_TYPES.SET_CATEGORIES,
                value: clone,
            });
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        }
    };

    const togglePicturesForm = async (item) => {
        if (!item) {
            dispatch({
                type: ACTION_TYPES.TOGGLE_IS_PICTURES_FORM_OPEN,
                value: null,
            });
            return;
        }

        const clone = cloneDeep(item);
        if (!clone.current?.vehicleInspectionItemId) {
            let payload = {
                vehicleInspectionItemId: null,
                reconInspectionItemId: clone.reconInspectionItemId,
                reconInspectionItemName: clone.name,
                pass: false,
                fail: false,
                part: false,
                repair: false,
                estimation: null,
                comment: null,
            };

            if (clone.current) {
                payload = {
                    ...payload,
                    ...clone.current,
                };
            }

            const { data } = await client.mutate({
                mutation: InventoryMutation.SAVE_VEHICLE_RECON_ITEMS,
                variables: {
                    stockNumber,
                    items: [payload],
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            const response = data?.saveVehicleReconItems;
            const { vehicleInspectionItemId } = response[0];
            clone.current = {
                ...payload,
                vehicleInspectionItemId,
            };
        }

        dispatch({
            type: ACTION_TYPES.TOGGLE_IS_PICTURES_FORM_OPEN,
            value: clone,
        });

        getImages({
            variables: {
                vehicleInspectionItemId: clone.current.vehicleInspectionItemId,
            },
        });
    };

    const onAddImages = async (files) => {
        const { current } = selectedItem;
        const dispatcherPayload = {
            type: ACTION_TYPES.TOGGLE_UPLOADING_IMAGES,
        };

        const { vehicleInspectionItemId } = current;
        const pathPrefix = vehicleInspectionItemId
            ? `${stockNumber}/inspection/${vehicleInspectionItemId}`
            : `${stockNumber > 0 ? `${stockNumber}` : ''}/inspection`;
        addImages(
            pathPrefix,
            files,
            dispatch,
            dispatcherPayload,
            null,
            'vehicleInspectionItemId',
            vehicleInspectionItemId,
            stockNumber,
            InventoryMutation.SAVE_VEHICLE_RECON_ITEM_IMAGES,
            getImages,
        );
    };

    const onSetPhotoIndex = (type) => setPhotoIndex(
        type,
        photoIndex,
        selectedItemImages,
        dispatch,
        ACTION_TYPES.SET_PHOTO_INDEX,
    );

    const onOpenImage = (index) => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_IMAGE_PREVIEW,
            value: index,
        });
    };

    const onDeleteImages = async (selectedImages, toggleImagesDeletionPrompt) => {
        const { current } = selectedItem;
        const payload = {
            variables: {
                vehicleInspectionItemId: current.vehicleInspectionItemId,
            },
        };

        deleteImages(
            selectedItemImages,
            'vehicleInspectionItemImageId',
            InventoryMutation.REMOVE_VEHICLE_RECON_ITEM_IMAGES,
            selectedImages,
            toggleImagesDeletionPrompt,
            getImages,
            payload,
        );
    };

    const toggleImagePreview = () => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_IMAGE_PREVIEW,
        });
    };

    const totalEstimate = formattedData.filter((el) => el.current?.repair).reduce((a, b) => a + (b.current?.estimation || 0), 0);
    const onChangeReconStatus = async (nextStatus, ins = null, inputNote, isApproving = false, isReapprovalRequest = false) => {
        const data = await changeReconStatus(
            stockNumber,
            nextStatus,
            ins,
            inputNote,
            isApproving,
            totalEstimate,
            maximumReconCost,
            saveReconItems,
            isReapprovalRequest,
        );

        if (data) {
            dispatch({
                type: ACTION_TYPES.SET_RECON_STATUS,
                value: data,
            });
        }
    };

    const StatusSelector = React.memo(({
        disabled,
        style = '',
        currentValue,
        inspectionItemId,
        field,
    }) => (
        <Checkbox
            color="default"
            className={style}
            disabled={disabled}
            checked={currentValue}
            onChange={(_, value) => onChange(field, value, inspectionItemId)}
        />
    ), (prevProps, nextProps) => prevProps.style === nextProps.style && prevProps.disabled === nextProps.disabled
    && prevProps.currentValue === nextProps.currentValue && prevProps.inspectionItemId === nextProps.inspectionItemId);

    const RepairSwitch = React.memo(({
        disabled,
        currentValue,
        inspectionItemId,
    }) => (
        <Switch
            disabled={disabled}
            checked={currentValue}
            onChange={(_, value) => onChange('repair', value, inspectionItemId)}
            inputProps={{ 'aria-label': 'primary checkbox' }}
        />
    ), (prevProps, nextProps) => prevProps.disabled === nextProps.disabled && prevProps.currentValue === nextProps.currentValue
    && prevProps.inspectionItemId === nextProps.inspectionItemId);

    const EstimateField = React.memo(({
        disabled,
        currentValue,
        inspectionItemId,
    }) => (
        <InputNumber
            disabled={disabled}
            value={currentValue}
            onBlur={(e) => onChange('estimation', Number(String(e.target.value ?? '').replace('$', '').replace(/,/g, '')), inspectionItemId)}
            placeholder="$0"
            showCurrency
            min={0}
            decimalScale={0}
            thousandSeparator
            size="sm"
        />
    ), (prevProps, nextProps) => prevProps.disabled === nextProps.disabled && prevProps.currentValue === nextProps.currentValue
    && prevProps.inspectionItemId === nextProps.inspectionItemId);

    const getColumns = () => [
        {
            label: 'Name',
            dataKey: 'name',
            width: 350,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;

                return (
                    <span className={isCategory ? classes.categoryText : ''}>{record.name}</span>
                );
            },
        },
        {
            label: 'Pass',
            dataKey: 'pass',
            width: 60,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { reconInspectionItemId, current } = record;

                    return (
                        <StatusSelector
                            disabled={false}
                            style={classes.passCheck}
                            currentValue={current?.pass || false}
                            inspectionItemId={reconInspectionItemId}
                            field="pass"
                        />
                    );
                }

                return (
                    <span className={classes.categoryText}>Pass</span>
                );
            },
        },
        {
            label: 'Fail',
            dataKey: 'fail',
            width: 60,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { reconInspectionItemId, current } = record;

                    return (
                        <StatusSelector
                            disabled={false}
                            style={classes.failCheck}
                            currentValue={current?.fail || false}
                            inspectionItemId={reconInspectionItemId}
                            field="fail"
                        />
                    );
                }

                return (
                    <span className={classes.categoryText}>Fail</span>
                );
            },
        },
        {
            label: 'Part',
            dataKey: 'part',
            width: 60,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { reconInspectionItemId, current } = record;

                    return (
                        <StatusSelector
                            disabled={current?.fail ? !current.fail : true}
                            currentValue={current?.part || false}
                            inspectionItemId={reconInspectionItemId}
                            field="part"
                        />
                    );
                }

                return (
                    <span className={classes.categoryText}>Part</span>
                );
            },
        },
        {
            label: 'Repair',
            dataKey: 'repair',
            width: 80,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { reconInspectionItemId, current } = record;

                    return (
                        <RepairSwitch
                            disabled={current?.fail ? !current.fail : true}
                            currentValue={current?.repair || false}
                            inspectionItemId={reconInspectionItemId}
                        />
                    );
                }

                return (
                    <span className={classes.categoryText}>Repair</span>
                );
            },
        },
        {
            label: 'Comments',
            dataKey: 'comments',
            width: 250,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { reconInspectionItemId, current } = record;

                    return (
                        <TextField
                            disabled={status === ReconStatus.INSPECTION_APPROVED || status === ReconStatus.REPAIRS_COMPLETE}
                            className={classes.textFieldSmall}
                            value={current?.comment || ''}
                            name="comment"
                            onChange={({ target: { name, value } }) => onChange(name, value, reconInspectionItemId)}
                            variant="outlined"
                            size="small"
                        />
                    );
                }

                return (
                    <span className={classes.categoryText}>Comments</span>
                );
            },
        },
        {
            label: 'Estimate',
            dataKey: 'estimate',
            width: 120,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { reconInspectionItemId, current } = record;

                    return (
                        <div className={current?.repair && (current?.estimation ?? 0) <= 0 ? classes.requiredEstimate : ''}>
                            <EstimateField
                                disabled={(current?.fail ? !current.fail : true)
                                    || status === ReconStatus.INSPECTION_APPROVED
                                    || status === ReconStatus.REPAIRS_COMPLETE}
                                currentValue={current?.estimation || 0}
                                inspectionItemId={reconInspectionItemId}
                            />
                        </div>
                    );
                }

                return (
                    <span className={classes.categoryText}>Estimate</span>
                );
            },
        },
        {
            label: 'Pictures',
            dataKey: 'pictures',
            width: 100,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const isCategory = 'reconInspectionCategoryId' in record;
                if (!isCategory) {
                    if (!canWrite) return null;
                    const { images } = record;

                    return (
                        <Button
                            size="small"
                            className={clsx(classes.containedSecondaryInfo, classes.uploadButton, (images || 0) > 0 ? classes.greenUploadButton : '')}
                            onClick={() => togglePicturesForm(record)}
                        >
                            {`View (${images || 0})`}
                        </Button>
                    );
                }

                return (
                    <span className={classes.categoryText}>Pictures</span>
                );
            },
        },
    ];

    const defaultInspector = inspector
        || (!canApprove ? inspectors.find((el) => el.default)?.userID : null);
    const inspectorOptions = inspectors
        .map((item) => ({
            value: item.userID,
            label: `${item.firstName} ${item.lastName}`,
        }));

    return !status ? null : (
        <div className={classes.main}>
            <If condition={isMobile}>
                {categories.map((cat, index) => {
                    if (!ArrayUtils.isNotEmpty(cat.items)) return null;

                    return (
                        <InspectionCategoryPanel
                            key={`${cat.name}-${index}`}
                            data={cat}
                            canWrite={canWrite}
                            onChange={onChange}
                            togglePicturesForm={togglePicturesForm}
                            isApproved={status === ReconStatus.INSPECTION_APPROVED || status === ReconStatus.REPAIRS_COMPLETE}
                        />
                    );
                })}
            </If>
            <If condition={!isMobile}>
                <div className={classes.tableContainer}>
                    <VirtualTable
                        loading={loadingInspectionListItems}
                        rowHeight={35}
                        totalRecords={formattedData.length}
                        data={formattedData}
                        columns={getColumns()}
                        rowStyleFormat={rowStyleFormat}
                    />
                </div>
            </If>
            <If condition={formattedData.length > 0}>
                <ReconActionsBar
                    showROAction={showROAction}
                    isSavingInspectionItems={isSavingInspectionItems}
                    canApprove={canApprove}
                    status={status}
                    note={note}
                    inspector={inspector}
                    defaultInspector={defaultInspector}
                    inspectorOptions={inspectorOptions}
                    totalEstimate={totalEstimate}
                    maximumReconCost={maximumReconCost}
                    onChangeReconStatus={onChangeReconStatus}
                    saveReconItems={saveReconItems}
                    savingReconStatus={savingReconStatus}
                    repairsSummary={repairsSummary}
                    roCreated={roCreated}
                    roNumber={roNumber}
                    stockNumber={stockNumber}
                    refetchReconStatus={refetchReconStatus}
                />
            </If>
            <InspectionImagesDialog
                isPicturesFormOpen={isPicturesFormOpen}
                isUploadingImages={isUploadingImages}
                openImagePreview={openImagePreview}
                addImages={onAddImages}
                canWrite={canWrite}
                togglePicturesForm={togglePicturesForm}
                selectedItemImages={selectedItemImages}
                onOpenImage={onOpenImage}
                photoIndex={photoIndex}
                toggleImagePreview={toggleImagePreview}
                setPhotoIndex={onSetPhotoIndex}
                onDeleteImages={onDeleteImages}
                moveType={moveType}
                isApproved={status === ReconStatus.INSPECTION_APPROVED || status === ReconStatus.REPAIRS_COMPLETE}
            />
        </div>
    );
};

InspectionListTab.propTypes = {
    stockNumber: PropTypes.number,
    canWrite: PropTypes.bool.isRequired,
    canApprove: PropTypes.bool.isRequired,
    saveRepairItem: PropTypes.func.isRequired,
    maximumReconCost: PropTypes.number,
    showROAction: PropTypes.bool,
};

InspectionListTab.defaultProps = {
    stockNumber: 0,
    maximumReconCost: 0,
    showROAction: false,
};

export default InspectionListTab;
